'use client'

import {
  createContext,
  type FC,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react'

interface ForgotPasswordPageContextType {
  email: string
  isForgotPasswordFormSubmitted: boolean
  isForgotPasswordRequestSuccess: boolean
  setEmail: (email: string) => void
  setIsForgotPasswordFormSubmitted: (success: boolean) => void
  setIsForgotPasswordRequestSuccess: (success: boolean) => void
}

const noop = (): never => {
  throw new Error(
    '[ForgotPasswordPageContext] Please use inside of <ForgotPasswordProvider>!',
  )
}

const defaultValue: ForgotPasswordPageContextType = {
  email: '',
  isForgotPasswordFormSubmitted: false,
  isForgotPasswordRequestSuccess: false,
  setEmail: noop,
  setIsForgotPasswordFormSubmitted: noop,
  setIsForgotPasswordRequestSuccess: noop,
}

const ForgotPasswordPageContext =
  createContext<ForgotPasswordPageContextType>(defaultValue)

interface ForgotPasswordPageProviderProps {
  children: ReactNode
}

export const ForgotPasswordPageContextProvider: FC<
  ForgotPasswordPageProviderProps
> = ({ children }) => {
  const [email, setEmail] = useState<string>('')
  const [isForgotPasswordRequestSuccess, setIsForgotPasswordRequestSuccess] =
    useState<boolean>(false)
  const [isForgotPasswordFormSubmitted, setIsForgotPasswordFormSubmitted] =
    useState<boolean>(false)

  const value = useMemo(
    () => ({
      email,
      isForgotPasswordFormSubmitted,
      isForgotPasswordRequestSuccess,
      setEmail,
      setIsForgotPasswordFormSubmitted,
      setIsForgotPasswordRequestSuccess,
    }),
    [email, isForgotPasswordFormSubmitted, isForgotPasswordRequestSuccess],
  )

  return (
    <ForgotPasswordPageContext.Provider value={value}>
      {children}
    </ForgotPasswordPageContext.Provider>
  )
}

export const useForgotPasswordPageContext =
  (): ForgotPasswordPageContextType => {
    return useContext(ForgotPasswordPageContext)
  }
